import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/Button'
import { useAuthStore } from '@/stores/authStore'
import { Input } from '@/components/ui/Input'
import { referralsService } from '@/services/referralsService'
import { IndicacaoClienteModal } from '@/pages/indicacoes/IndicacaoClienteModal'
import { IndicadorCadastroModal } from '@/pages/indicacoes/IndicadorCadastroModal'
import { IndicacoesTable } from '@/pages/indicacoes/IndicacoesTable'
import {
  STATUS_FILTER_OPTIONS,
  TIPO_FILTER_OPTIONS,
  filterIndicacoes,
  indicadorDaLinha,
  indicadorOptions,
  comercialOptions,
  SEM_COMERCIAL,
  type IndicacaoStaffRow,
} from '@/pages/indicacoes/indicacoesFilters'
import { abaDaIndicacao, type IndicacaoAba } from '@/pages/indicacoes/indicacaoAbas'
import { extractApiError } from '@/utils/extractApiError'
import { Search, UserPlus, Users, Target, FileSpreadsheet } from 'lucide-react'
import api from '@/services/api'
import { ParceirosImportModal } from '@/pages/parceiros/ParceirosImportModal'

const selectClass = 'h-10 rounded-md border border-[var(--glass-border)] bg-transparent px-3 text-sm text-[var(--font-color-body)]'
const PAGE_SIZE = 50
const ABAS: Array<{ id: IndicacaoAba; label: string }> = [
  { id: 'abertas', label: 'Em aberto' },
  { id: 'descartados', label: 'Descartados' },
  { id: 'nao_fecharam', label: 'Não fecharam' },
]

type StaffListPayload = {
  data?: IndicacaoStaffRow[]
  total?: number
  totalPages?: number
}

export default function Indicacoes() {
  const user = useAuthStore((s) => s.user)
  const [rows, setRows] = useState<IndicacaoStaffRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [indicador, setIndicador] = useState('')
  const [comercial, setComercial] = useState('')
  const [tipo, setTipo] = useState('')
  const [status, setStatus] = useState('')
  const [aba, setAba] = useState<IndicacaoAba>('abertas')
  const [message, setMessage] = useState('')
  const [cadastroOpen, setCadastroOpen] = useState(false)
  const [clienteOpen, setClienteOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [partners, setPartners] = useState<Array<{ id: string; nome: string; empresa?: string | null }>>([])
  const [sendingId, setSendingId] = useState<string | null>(null)
  const [discardingId, setDiscardingId] = useState<string | null>(null)
  const loadSeq = useRef(0)

  async function sendToCarteira(row: IndicacaoStaffRow) {
    setSendingId(row.id)
    setMessage('')
    try {
      const result = await referralsService.sendToCarteira(row.id)
      setRows((current) => current.map((item) => (
        item.id === row.id
          ? { ...item, leadInstance: { id: result.leadInstanceId, closerId: user?.id ?? item.leadInstance?.closerId ?? null, deletedAt: null } }
          : item
      )))
      toast.success(result.already ? 'Este lead já está na Minha carteira.' : 'Lead enviado para a Minha carteira.')
    } catch {
      setMessage('Não foi possível enviar o lead para a Minha carteira.')
    } finally {
      setSendingId(null)
    }
  }

  async function discard(row: IndicacaoStaffRow) {
    if (!window.confirm(`Descartar o lead de ${row.nome}? Ele sai da lista em aberto.`)) return
    setDiscardingId(row.id)
    setMessage('')
    try {
      await referralsService.discardStaff(row.id)
      setRows((current) => current.map((item) => (
        item.id === row.id
          ? {
              ...item,
              publicStatus: 'perdido',
              leadInstance: item.leadInstance
                ? { ...item.leadInstance, status: 'DESCARTADO' }
                : { id: '', closerId: null, deletedAt: null, status: 'DESCARTADO' },
            }
          : item
      )))
      toast.success('Lead descartado.')
    } catch (err) {
      setMessage(extractApiError(err, 'Não foi possível descartar o lead.').message)
    } finally {
      setDiscardingId(null)
    }
  }

  async function load() {
    const seq = ++loadSeq.current
    const first = await referralsService.listStaff(1)
    if (seq !== loadSeq.current) return
    const pack = first.data as StaffListPayload | undefined
    const totalPages = pack?.totalPages ?? 1
    const firstRows = pack?.data ?? []
    setTotal(pack?.total ?? firstRows.length)
    setRows(firstRows)
    setPage(1)
    if (totalPages <= 1) return
    const rest = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) =>
        referralsService.listStaff(index + 2).then((resp) => (resp.data as StaffListPayload | undefined)?.data ?? []),
      ),
    )
    if (seq !== loadSeq.current) return
    setRows(firstRows.concat(...rest))
  }

  useEffect(() => {
    load().catch(() => setMessage('Não foi possível carregar as indicações.'))
  }, [])

  useEffect(() => {
    setPage(1)
  }, [search, indicador, comercial, tipo, status, aba])

  useEffect(() => {
    if (!importOpen) return
    api.get('/parceiros/options').then((resp) => setPartners(resp.data.data ?? [])).catch(() => setPartners([]))
  }, [importOpen])

  const daAba = rows.filter((row) => abaDaIndicacao(row) === aba)
  const showComercial = user?.role === 'admin' || user?.role === 'manager'
  const filteredRows = filterIndicacoes(daAba, {
    search,
    indicador,
    comercial: showComercial ? comercial : '',
    tipo,
    status,
  })
  const contagem = {
    abertas: rows.filter((row) => abaDaIndicacao(row) === 'abertas').length,
    descartados: rows.filter((row) => abaDaIndicacao(row) === 'descartados').length,
    nao_fecharam: rows.filter((row) => abaDaIndicacao(row) === 'nao_fecharam').length,
  }
  const hasFilters = Boolean(search.trim() || indicador || (showComercial && comercial) || tipo || status)
  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE))
  const safePage = Math.min(page, pageCount)
  const visibleRows = filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)
  const indicadores = indicadorOptions(rows)
  const comerciais = comercialOptions(rows)
  const semComercial = rows.some((row) => !row.referrer?.responsavelUser?.name?.trim())
  const uniqueIndicators = new Set(rows.map((row) => indicadorDaLinha(row)).filter(Boolean)).size
  const wonRows = rows.filter((row) => row.publicStatus === 'ganho' || row.publicStatus === 'pago').length

  return (
    <DashboardLayout
      title='Indicações'
      actions={
        <div className='flex gap-2'>
          <Button variant='secondary' size='sm' icon={<FileSpreadsheet size={13} />} onClick={() => setImportOpen(true)}>Importar planilha</Button>
          <Button variant='secondary' size='sm' onClick={() => setClienteOpen(true)}>Registrar cliente</Button>
          <Button size='sm' icon={<UserPlus size={13} />} onClick={() => setCadastroOpen(true)}>Cadastrar indicador</Button>
        </div>
      }
    >
      {message && <p className='mb-4 text-sm text-rose-500'>{message}</p>}

      <div className='mb-4 grid grid-cols-1 gap-4 md:grid-cols-3'>
        {[
          { label: 'Total de indicações', value: total, icon: Target, tone: 'text-indigo-400', bg: 'bg-indigo-500/10' },
          { label: 'Indicadores', value: uniqueIndicators, icon: Users, tone: 'text-teal-400', bg: 'bg-teal-500/10' },
          { label: 'Fechadas', value: wonRows, icon: UserPlus, tone: 'text-emerald-400', bg: 'bg-emerald-500/10' },
        ].map((card) => (
          <div key={card.label} className='glass-card p-4'>
            <div className='mb-2 flex items-center justify-between'>
              <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${card.bg} ${card.tone}`}>
                <card.icon size={20} />
              </div>
            </div>
            <p className='mb-1 text-[var(--font-color-label)] uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
              {card.label}
            </p>
            <p className={`text-2xl font-black ${card.tone}`}>{card.value}</p>
          </div>
        ))}
      </div>

      <div className='glass-card mb-4 flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center'>
        <div className='flex-1'>
          <Input
            placeholder={showComercial ? 'Buscar por cliente, indicador, comercial ou empresa...' : 'Buscar por cliente, indicador, empresa ou tipo...'}
            icon={<Search size={14} />}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <select className={selectClass} aria-label='Filtrar por indicador' value={indicador} onChange={(event) => setIndicador(event.target.value)}>
          <option value=''>Indicador</option>
          {indicadores.map((nome) => <option key={nome} value={nome}>{nome}</option>)}
        </select>
        {showComercial && (
          <select className={selectClass} aria-label='Filtrar por comercial' value={comercial} onChange={(event) => setComercial(event.target.value)}>
            <option value=''>Comercial</option>
            {comerciais.map((nome) => <option key={nome} value={nome}>{nome}</option>)}
            {semComercial && <option value={SEM_COMERCIAL}>Sem comercial</option>}
          </select>
        )}
        <select className={selectClass} aria-label='Filtrar por tipo' value={tipo} onChange={(event) => setTipo(event.target.value)}>
          <option value=''>Tipo</option>
          {TIPO_FILTER_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <select className={selectClass} aria-label='Filtrar por status' value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value=''>Status</option>
          {STATUS_FILTER_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        {hasFilters && (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => { setSearch(''); setIndicador(''); setComercial(''); setTipo(''); setStatus(''); setPage(1) }}
          >
            Limpar
          </Button>
        )}
      </div>

      <div className='mb-4 flex flex-wrap gap-2' role='tablist' aria-label='Situação dos leads'>
        {ABAS.map((item) => (
          <button
            key={item.id}
            type='button'
            role='tab'
            aria-selected={aba === item.id}
            onClick={() => setAba(item.id)}
            className={`rounded-full px-4 py-2 text-sm ${
              aba === item.id
                ? 'bg-[var(--brand-primary)] text-white'
                : 'border border-[var(--glass-border)] text-[var(--font-color-body)]'
            }`}
          >
            {item.label} ({contagem[item.id]})
          </button>
        ))}
      </div>

      <IndicacoesTable
        rows={visibleRows}
        aba={aba}
        hasFilters={hasFilters}
        page={safePage}
        pageCount={pageCount}
        pageSize={PAGE_SIZE}
        filteredCount={filteredRows.length}
        userId={user?.id}
        role={user?.role}
        showComercial={showComercial}
        sendingId={sendingId}
        discardingId={discardingId}
        onPage={setPage}
        onCarteira={(row) => { void sendToCarteira(row) }}
        onDiscard={(row) => { void discard(row) }}
      />

      <IndicadorCadastroModal
        open={cadastroOpen}
        onClose={() => setCadastroOpen(false)}
        onSaved={() => setMessage('Indicador cadastrado. O convite foi enviado por e-mail.')}
      />
      <IndicacaoClienteModal
        open={clienteOpen}
        onClose={() => setClienteOpen(false)}
        onSaved={() => { setMessage('Cliente indicado registrado.'); void load() }}
      />
      <ParceirosImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        partners={partners}
        onImported={() => { void load() }}
      />
    </DashboardLayout>
  )
}
